import ModelStore from './stores/model-store';
import ParamStore from './stores/param-store';
import UiStateStore from './stores/ui-state-store';
import UiState from '../constants/ui-state';
import Validator from './validator';
import FormType from '../constants/form-type';
import PasswordMode from "../constants/password-mode";
import InitialCohort from '../constants/initial-cohort';
import ClsProviderType from '../constants/cls-provider-type';
import ClsServices from '../ws/cls-services';
import FormUiState from './form-ui-state';
import TrackingAction from '../constants/tracking-action';
import JmServices from '../ws/jm-services';

import {localize} from '../infrastructure/localization';

export default class FormActions {

    static changeFormType(componentId, formType) {
        ModelStore.update(componentId, (model) => {
            if (model.formType != formType) {
                Validator.set(model, ModelStore.emptyValidationModel);
            }
            model.formType = formType;

            model.password = '';
            model.confirmedPassword = '';
            model.keepLoggedIn = false;
            model.acceptTerms = false;
            model.sendProductInformation = true;
        });

        UiStateStore.update(componentId, (model) => {
            model.form.showProductInformation = false;
        });
    }

    static startEditEmail(componentId) {
        Validator.enable(componentId);
    }

    static endEditEmail(componentId) {
        ModelStore.updateAsync(componentId, (model) => {
            model.validation.hasEmailBeenEdited = true;
            Validator.clearSubmitError(model);
            return Validator.validateEmailAsync(componentId, model);
        });
    }

    static changeEmail(componentId, value) {
        ModelStore.updateAsync(componentId, (model) => {
            model.email = value;
            Validator.clearSubmitError(model);
            return Validator.validateEmailAsync(componentId, model);
        });
    }

    static endEditPassword(componentId) {
        ModelStore.update(componentId, (model) => {
            model.validation.hasPasswordBeenEdited = true;
            Validator.clearSubmitError(model);
            return Validator.validatePassword(model);
        });
    }

    static changePassword(componentId, value) {
        ModelStore.update(componentId, (model) => {
            model.password = value;
            Validator.clearSubmitError(model);
            Validator.validatePassword(model);
        });
    }

    static clearPassword(componentId) {
        ModelStore.update(componentId, (model) => {
            model.password = '';
            Validator.validatePassword(model);
        });
    }

    static endEditConfirmedPassword(componentId) {
        ModelStore.update(componentId, (model) => {
            model.validation.hasConfirmedPasswordBeenEdited = true;
            Validator.clearSubmitError(model);
            return Validator.validateConfirmedPassword(model);
        });
    }

    static changeConfirmedPassword(componentId, value) {
        ModelStore.update(componentId, (model) => {
            model.confirmedPassword = value;
            Validator.clearSubmitError(model);
            Validator.validateConfirmedPassword(model);
        });
    }

    static changeKeepLoggedIn(componentId) {
        ModelStore.update(componentId, (model) => {
            model.keepLoggedIn = !model.keepLoggedIn;
            Validator.clearSubmitError(model);
        });
    }

    static changeAcceptTerms(componentId) {
        ModelStore.update(componentId, (model) => {
            model.acceptTerms = !model.acceptTerms;
            Validator.clearSubmitError(model);
            Validator.validateAcceptTerms(model);
        });
    }

    static changeSendProductInformation(componentId) {
        ModelStore.update(componentId, (model) => {
            model.sendProductInformation = !model.sendProductInformation;
            Validator.clearSubmitError(model);
            if (!model.sendProductInformation) {
                JmServices.trackEvent(componentId, { trackingValue: TrackingAction.jlsSendMeProductInformationUnchecked });
            }
        });
    }

    static register(componentId) {
        if (UiStateStore.getModel(componentId).form.uiState !== UiState.ready) {
            return;
        }

        Validator.enable();

        return FormActions.validateAllAsync(componentId)
            .then(() => {
                Validator.clearSubmitError(ModelStore.getModel(componentId));
            })
            .then(() => {
                if (ModelStore.getModel(componentId).validation.errors.acceptTerms !== '') {
                    JmServices.trackEvent(componentId, { trackingValue: TrackingAction.jlsGetStartedButtonClickedButFormIsInvalidDueToTcsCheckboxNotChecked });
                }

                if (ModelStore.getModel(componentId).validation.hasErrors) {
                    JmServices.trackEvent(componentId, { trackingValue: TrackingAction.jlsGetStartedButtonClickedButFormIsInvalid });
                    return new Promise((resolve) => resolve());
                }

                JmServices.trackEvent(componentId, { trackingValue: TrackingAction.jlsGetStartedButtonClickedAndFormIsValid });

                FormUiState.setWaiting(componentId);

                return ClsServices.register(componentId, {
                    email: ModelStore.getModel(componentId).email,
                    password: ParamStore.getModel(componentId).initialCohort === InitialCohort.passwordlessRegistration
                        ? null : ModelStore.getModel(componentId).password,
                    sendProductInformation: ModelStore.getModel(componentId).sendProductInformation,
                    keepLoggedIn: true, // By default registration persists KeepMeLoggedIn option at CLS side
                    activate: ParamStore.getModel(componentId).initialCohort === InitialCohort.passwordlessRegistration
                }).then(
                    response => {
                        if (response && response.error) {
                            ModelStore.updateAsync(componentId, (model) => {
                                model.validation.errors.submitError = localize('error.generic');

                                return Validator.validateAllAsync(componentId, model)
                                    .then(() => FormUiState.setReady(componentId));
                            })
                        }
                    }
                );
            });
    }

    static login(componentId) {
        if(UiStateStore.getModel(componentId).form.uiState !== UiState.ready) {
            return;
        }

        Validator.enable();

        return FormActions.validateAllAsync(componentId)
            .then(() => {
                Validator.clearSubmitError(ModelStore.getModel(componentId));
            })
            .then(() => {
                if (ModelStore.getModel(componentId).validation.hasErrors) {
                    // TODO: Tracking
                    return new Promise((resolve) => resolve());
                }

                // TODO: Tracking

                FormUiState.setWaiting(componentId);

                return ClsServices.login(componentId, {
                    email: ModelStore.getModel(componentId).email,
                    password: ModelStore.getModel(componentId).password,
                    keepLoggedIn: ModelStore.getModel(componentId).keepLoggedIn,
                    clsProviderType: ClsProviderType.standardProviderId
                }).then(
                    response => {
                        if (response && response.error) {
                            ModelStore.updateAsync(componentId, (model) => {

                                if (response.invalidCredentials) {
                                    model.validation.errors.email = localize('validation.invalid-credentials');
                                    model.validation.hasErrors = true;

                                    FormActions.clearPassword(componentId);

                                    return new Promise((resolve) => resolve()).then(() => FormUiState.setReady(componentId));
                                }

                                model.validation.errors.submitError = localize('error.generic');

                                return Validator.validateAllAsync(componentId, model)
                                    .then(() => FormUiState.setReady(componentId));
                            })
                        }
                    }
                );
            });
    }

    static forgotPassword(componentId) {
        if(UiStateStore.getModel(componentId).form.uiState !== UiState.ready) {
            return;
        }

        Validator.enable();

        return FormActions.validateAllAsync(componentId);
    }

    static validateAllAsync(componentId) {
        return ModelStore.updateAsync(componentId, (model) => {
            model.validation.hasEmailBeenEdited = true;
            model.validation.hasPasswordBeenEdited = model.passwordMode !== PasswordMode.passwordless;
            model.validation.hasConfirmedPasswordBeenEdited = model.passwordMode === PasswordMode.standard;

            return Validator.validateAllAsync(componentId, model);
        });
    }

    static redirectToForgotPwd(componentId) {
        // Dirty lines to not wire own forgotPwd form and use existing login url to easier avoid breaking changes with
        // implementers who cannot increment component version and add new mandatory component parameters
        // at the same time.
        window.top.location = `${ParamStore.getModel(componentId).clsEndpoint}forgotpassword.aspx${ParamStore.getModel(componentId).clsLoginUrl.replace('/login.aspx', '').replace(/&amp;/g, '&')}&force=1&email=${encodeURIComponent(ModelStore.getModel(componentId).email)}`;
    }
}


/** WEBPACK FOOTER **
 ** ./src/logic/form-actions.js
 **/